import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../../firebase";
import {
	collection,
	query,
	where,
	orderBy,
	onSnapshot,
	Timestamp,
} from "firebase/firestore";
import { useTranslation } from "../../i18n/i18n";
import useLocale from "../../hooks/useLocale";
import { formatDateLocalized } from "../../utils/date";
import {
	CalendarSync,
	ArrowRight,
	Utensils,
	Home,
	HeartPulse,
	User,
	Euro,
	Circle,
	FileText,
	PiggyBank,
} from "lucide-react";

const DAYS_AHEAD = 7; // Show recurring transactions with nextDate within the next 7 days

const categoryIcons = {
	food: <Utensils size={14} />,
	housing: <Home size={14} />,
	health: <HeartPulse size={14} />,
	personal: <User size={14} />,
	income: <Euro size={14} />,
	savings: <PiggyBank size={14} />,
	general: <FileText size={14} />,
	other: <Circle size={14} />,
};

const UpcomingRecurring = () => {
	const { translate } = useTranslation();
	const { locale } = useLocale();
	const navigate = useNavigate();
	const [upcomingTransactions, setUpcomingTransactions] = useState([]);
	const [loading, setLoading] = useState(true);

	const language = useMemo(
		() => (locale.startsWith("fr") ? "fr" : "en"),
		[locale]
	);

	const formatCurrency = useCallback(
		amount =>
			new Intl.NumberFormat(language === "fr" ? "fr-FR" : "en-GB", {
				style: "currency",
				currency: "EUR",
				minimumFractionDigits: 2,
			}).format(amount),
		[language]
	);

	const formatNextDate = useCallback(
		timestamp => {
			if (!timestamp) return "";
			const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
			return formatDateLocalized(date, locale);
		},
		[locale]
	);

	useEffect(() => {
		const userId = auth.currentUser?.uid;
		if (!userId) {
			setLoading(false);
			return;
		}

		// Fetch active recurring transactions with nextDate within the next DAYS_AHEAD days
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		const futureDate = new Date(today);
		futureDate.setDate(futureDate.getDate() + DAYS_AHEAD);
		futureDate.setHours(23, 59, 59, 999);

		const q = query(
			collection(db, "recurringTransactions"),
			where("userId", "==", userId),
			where("isActive", "==", true),
			where("nextDate", ">=", Timestamp.fromDate(today)),
			where("nextDate", "<=", Timestamp.fromDate(futureDate)),
			orderBy("nextDate", "asc")
		);

		const unsubscribe = onSnapshot(
			q,
			snapshot => {
				const transactions = snapshot.docs.map(doc => ({
					id: doc.id,
					...doc.data(),
				}));
				setUpcomingTransactions(transactions);
				setLoading(false);
			},
			err => {
				console.error("Error fetching upcoming recurring transactions:", err);
				setLoading(false);
			}
		);

		return () => unsubscribe();
	}, []);

	// Returns label like "Today", "Tomorrow", or the formatted date
	const getDayLabel = useCallback(
		timestamp => {
			if (!timestamp) return "";
			const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);

			const today = new Date();
			today.setHours(0, 0, 0, 0);
			const tomorrow = new Date(today);
			tomorrow.setDate(tomorrow.getDate() + 1);

			const targetDay = new Date(date);
			targetDay.setHours(0, 0, 0, 0);

			if (targetDay.getTime() === today.getTime()) {
				return translate("recurring.upcoming.today");
			}
			if (targetDay.getTime() === tomorrow.getTime()) {
				return translate("recurring.upcoming.tomorrow");
			}
			return formatNextDate(timestamp);
		},
		[translate, formatNextDate]
	);

	if (loading || upcomingTransactions.length === 0) return null;

	return (
		<div className="upcoming-recurring-section">
			<div className="upcoming-recurring-header">
				<span className="upcoming-recurring-title">
					<CalendarSync size={18} />
					{translate("recurring.upcoming.title")}
				</span>
				<button
					className="upcoming-recurring-link"
					onClick={() => navigate("/recurring-transactions")}
					type="button"
				>
					{translate("recurring.upcoming.seeAll")}
					<ArrowRight size={14} />
				</button>
			</div>

			<div className="upcoming-recurring-list">
				{upcomingTransactions.map(transaction => (
					<div
						key={transaction.id}
						className={`upcoming-recurring-item upcoming-type-${transaction.type}`}
					>
						<div className="upcoming-recurring-left">
							<span
								className={`upcoming-category-icon category-${transaction.category}`}
							>
								{categoryIcons[transaction.category] || categoryIcons["other"]}
							</span>
							<span className="upcoming-recurring-name">
								{transaction.title}
							</span>
						</div>
						<div className="upcoming-recurring-right">
							<span className={`upcoming-recurring-amount ${transaction.type}`}>
								{transaction.type === "expense" ? "−" : "+"}
								{formatCurrency(Math.abs(transaction.amount))}
							</span>
							<span className="upcoming-recurring-date">
								{getDayLabel(transaction.nextDate)}
							</span>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

export default UpcomingRecurring;
